import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  ProCaseCostView, ProCaseDecisionContext, ProCaseStatus, ProCaseStepView, ProCaseView,
  ProChecklistItemView, ProChecklistRuleView, ProCostSummary, ProStatusCount, ProStepDuration,
  ProStepView, ProTemplateView,
} from '../types'

const TEMPLATES = '/v1/pro/templates'
const CASES = '/v1/pro/cases'

export function useProTemplates() {
  return useQuery({
    queryKey: ['pro', 'templates'],
    queryFn: async () => (await apiClient.get<ProTemplateView[]>(TEMPLATES)).data,
  })
}

export function useProSteps(templateId: string | undefined) {
  return useQuery({
    queryKey: ['pro', 'templates', templateId, 'steps'],
    enabled: templateId != null,
    queryFn: async () => (await apiClient.get<ProStepView[]>(`${TEMPLATES}/${templateId}/steps`)).data,
  })
}

export function useProChecklistRules(templateId: string | undefined) {
  return useQuery({
    queryKey: ['pro', 'templates', templateId, 'rules'],
    enabled: templateId != null,
    queryFn: async () =>
      (await apiClient.get<ProChecklistRuleView[]>(`${TEMPLATES}/${templateId}/checklist-rules`)).data,
  })
}

export function useProTemplateMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['pro'] })

  return {
    create: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<ProTemplateView>(TEMPLATES, body)).data,
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<ProTemplateView>(`${TEMPLATES}/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    replaceSteps: useMutation({
      mutationFn: async ({ id, steps }: { id: string; steps: Record<string, unknown>[] }) =>
        (await apiClient.put<ProStepView[]>(`${TEMPLATES}/${id}/steps`, { steps })).data,
      onSuccess: invalidate,
    }),
    addRule: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.post<ProChecklistRuleView>(`${TEMPLATES}/${id}/checklist-rules`, body)).data,
      onSuccess: invalidate,
    }),
    deleteRule: useMutation({
      mutationFn: async (ruleId: string) => {
        await apiClient.delete(`${TEMPLATES}/checklist-rules/${ruleId}`)
      },
      onSuccess: invalidate,
    }),
  }
}

export function useProCases(status?: ProCaseStatus, assignedToUserId?: string, templateId?: string) {
  return useQuery({
    queryKey: ['pro', 'cases', status, assignedToUserId, templateId],
    queryFn: async () =>
      (await apiClient.get<ProCaseView[]>(CASES, {
        params: {
          status: status || undefined,
          assignedToUserId: assignedToUserId || undefined,
          templateId: templateId || undefined,
        },
      })).data,
  })
}

/** The case, its steps, its checklist and its costs — one call, so nothing renders half-loaded. */
export function useProCaseContext(id: string | null) {
  return useQuery({
    queryKey: ['pro', 'cases', id, 'context'],
    enabled: id != null,
    queryFn: async () =>
      (await apiClient.get<ProCaseDecisionContext>(`${CASES}/${id}/context`)).data,
  })
}

export function useProCaseMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['pro'] })
    void queryClient.invalidateQueries({ queryKey: ['documents'] })
  }

  return {
    open: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<ProCaseView>(CASES, body)).data,
      onSuccess: invalidate,
    }),
    move: useMutation({
      mutationFn: async ({ id, status }: { id: string; status: ProCaseStatus }) =>
        (await apiClient.post<ProCaseView>(`${CASES}/${id}/status`, { status })).data,
      onSuccess: invalidate,
    }),
    assign: useMutation({
      mutationFn: async ({ id, userId }: { id: string; userId: string | null }) =>
        (await apiClient.post<ProCaseView>(`${CASES}/${id}/assign`, { userId })).data,
      onSuccess: invalidate,
    }),
    startStep: useMutation({
      mutationFn: async (stepId: string) =>
        (await apiClient.post<ProCaseStepView>(`${CASES}/steps/${stepId}/start`)).data,
      onSuccess: invalidate,
    }),
    completeStep: useMutation({
      mutationFn: async ({ stepId, referenceNo, notes }: { stepId: string; referenceNo?: string; notes?: string }) =>
        (await apiClient.post<ProCaseStepView>(`${CASES}/steps/${stepId}/complete`, { referenceNo, notes })).data,
      onSuccess: invalidate,
    }),
    satisfyItem: useMutation({
      mutationFn: async ({ itemId, ...body }: { itemId: string } & Record<string, unknown>) =>
        (await apiClient.post<ProChecklistItemView>(`${CASES}/checklist/${itemId}/satisfy`, body)).data,
      onSuccess: invalidate,
    }),
    untickItem: useMutation({
      mutationFn: async (itemId: string) =>
        (await apiClient.post<ProChecklistItemView>(`${CASES}/checklist/${itemId}/untick`)).data,
      onSuccess: invalidate,
    }),
    addCost: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.post<ProCaseCostView>(`${CASES}/${id}/costs`, body)).data,
      onSuccess: invalidate,
    }),
    complete: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.post<ProCaseView>(`${CASES}/${id}/complete`, body)).data,
      onSuccess: invalidate,
    }),
  }
}

export function useProReports(from: string, to: string, templateId?: string) {
  const byStatus = useQuery({
    queryKey: ['pro', 'reports', 'status'],
    queryFn: async () => (await apiClient.get<ProStatusCount[]>(`${CASES}/reports/by-status`)).data,
  })
  const stepDurations = useQuery({
    queryKey: ['pro', 'reports', 'steps', templateId],
    queryFn: async () =>
      (await apiClient.get<ProStepDuration[]>(`${CASES}/reports/step-durations`, {
        params: { templateId: templateId || undefined },
      })).data,
  })
  const costs = useQuery({
    queryKey: ['pro', 'reports', 'costs', from, to],
    queryFn: async () =>
      (await apiClient.get<ProCostSummary[]>(`${CASES}/reports/cost-per-case`, { params: { from, to } })).data,
  })
  return { byStatus, stepDurations, costs }
}
